import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Check, Loader2 } from "lucide-react";
import { Section, Reveal, Eyebrow } from "@/components/primitives";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useWhatsApp } from "@/lib/useConfig";
import { toast } from "sonner";

const serviceOptions = [
  "Vorrei un sito internet",
  "Vorrei farmi trovare su Google",
  "Vorrei gestire i miei social",
  "Vorrei tutto",
  "Non so ancora, voglio capire",
];

export default function Contatti() {
  const WHATSAPP_NUMBER = useWhatsApp();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const interesse = searchParams.get("interesse");
    if (interesse) {
      setForm((f) => ({
        ...f,
        service: "Vorrei un sito internet",
        message: `Ciao MC Studio, sono interessato a: ${interesse}.\nVorrei maggiori informazioni.`,
      }));
    }
  }, [searchParams]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const waHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        "Ciao MC Studio! Vorrei la bozza gratis del sito per la mia attività."
      )}`
    : "#";

  return (
    <div data-testid="contatti-page">
      <Section className="pt-20 pb-12">
        <Reveal>
          <Eyebrow>Contatti</Eyebrow>
          <h1 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
            Chiedi la tua bozza gratis.
          </h1>
          <p className="mt-6 text-lg text-ink/70 max-w-2xl">
            Scrivici due righe sulla tua attività: ti facciamo vedere com'è il
            tuo sito, gratis e senza impegno. Ti rispondiamo entro un giorno.
          </p>
        </Reveal>
      </Section>

      <Section className="pb-24">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <Reveal className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              {sent ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-coral/10 flex items-center justify-center text-coral">
                    <Check size={32} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-2xl text-ink">Messaggio inviato!</h3>
                  <p className="mt-2 text-ink/60">Grazie per averci contattato. Ti risponderemo al più presto.</p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="mt-6 text-coral font-semibold hover:underline"
                  >
                    Invia un altro messaggio
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">Nome *</label>
                      <input
                        name="name" value={form.name} onChange={handle}
                        data-testid="contact-name"
                        className="w-full rounded-xl border border-ink/15 bg-beige/40 px-4 py-3 outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">Email *</label>
                      <input
                        name="email" type="email" value={form.email} onChange={handle}
                        data-testid="contact-email"
                        className="w-full rounded-xl border border-ink/15 bg-beige/40 px-4 py-3 outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        placeholder="mario@azienda.it"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">Telefono</label>
                      <input
                        name="phone" value={form.phone} onChange={handle}
                        data-testid="contact-phone"
                        className="w-full rounded-xl border border-ink/15 bg-beige/40 px-4 py-3 outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        placeholder="+39 ..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">Servizio di interesse</label>
                      <select
                        name="service" value={form.service} onChange={handle}
                        data-testid="contact-service"
                        className="w-full rounded-xl border border-ink/15 bg-beige/40 px-4 py-3 outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                      >
                        <option value="">Seleziona...</option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">Messaggio *</label>
                    <textarea
                      name="message" value={form.message} onChange={handle} rows={5}
                      data-testid="contact-message"
                      className="w-full rounded-xl border border-ink/15 bg-beige/40 px-4 py-3 outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition resize-none"
                      placeholder="Es: Ho un'attività e vorrei un sito con i miei servizi, contatti e orari..."
                    />
                  </div>
                  <button
                    type="submit" disabled={loading}
                    data-testid="contact-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full px-8 py-4 font-semibold hover:bg-[#e04047] transition-colors disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Invio...</> : "Invia richiesta"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="bg-ink text-beige rounded-2xl p-8 lg:p-10 h-full flex flex-col">
              <h3 className="font-display font-bold text-2xl">Scrivici direttamente</h3>
              <p className="mt-3 text-beige/60 text-sm">Preferisci parlarci subito? Siamo qui per te.</p>
              <ul className="mt-8 space-y-5">
                <li className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-beige/10 flex items-center justify-center text-coral"><Phone size={20} /></div>
                  <div><div className="text-xs text-beige/50 uppercase tracking-widest">Telefono</div><div className="font-medium">+39 375 9920268</div></div>
                </li>
                <li className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-beige/10 flex items-center justify-center text-coral"><Mail size={20} /></div>
                  <div><div className="text-xs text-beige/50 uppercase tracking-widest">Email</div><div className="font-medium">info@mcstudio.it</div></div>
                </li>
                <li className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-beige/10 flex items-center justify-center text-coral"><MapPin size={20} /></div>
                  <div><div className="text-xs text-beige/50 uppercase tracking-widest">Dove</div><div className="font-medium">Italia · da remoto</div></div>
                </li>
              </ul>
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="contatti-whatsapp-cta"
                className="mt-auto pt-8 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-4 font-semibold hover:opacity-90 transition-opacity"
              >
                Scrivici su WhatsApp
              </a>
              <p className="mt-3 text-center text-beige/40 text-xs">Bozza gratis · ti rispondiamo entro un giorno</p>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
